/**
 * Contas da série de uso do solo para a ficha do foco: porcentagens por ano,
 * as classes que merecem cor própria no gráfico e o resumo em texto.
 */
import type { ComposicaoAno, ImovelCar } from './api';
import { classeMapbiomas } from './legenda';

/** Código das classes juntadas em "Outras" no gráfico. */
export const OUTRAS = -1;

export interface Fatia {
  codigo: number;
  /** 0–1 dos pixels mapeados no círculo. */
  frac: number;
}

export function porcentagens(comp: ComposicaoAno): Fatia[] {
  const total = comp.reduce((s, [, n]) => s + n, 0);
  if (!total) return [];
  return comp.map(([codigo, n]) => ({ codigo, frac: n / total })).sort((a, b) => b.frac - a.frac || a.codigo - b.codigo);
}

/**
 * Classes com cor própria: as que chegam a `minimo` em algum ano, das mais
 * presentes na série para as menos, no máximo `max`. O resto vira "Outras".
 */
export function classesDestaque(serie: ComposicaoAno[], minimo = 0.05, max = 6): number[] {
  const peso = new Map<number, number>();
  const pico = new Map<number, number>();
  for (const comp of serie) {
    for (const { codigo, frac } of porcentagens(comp)) {
      peso.set(codigo, (peso.get(codigo) ?? 0) + frac);
      pico.set(codigo, Math.max(pico.get(codigo) ?? 0, frac));
    }
  }
  return [...peso.keys()]
    .filter((c) => (pico.get(c) ?? 0) >= minimo)
    .sort((a, b) => peso.get(b)! - peso.get(a)! || a - b)
    .slice(0, max);
}

/** As fatias de um ano no gráfico: as de destaque, na ordem delas, e "Outras" no fim. */
export function fatiasDoAno(comp: ComposicaoAno, destaque: number[]): Fatia[] {
  const pct = porcentagens(comp);
  const fatias: Fatia[] = [];
  for (const codigo of destaque) {
    const f = pct.find((p) => p.codigo === codigo);
    if (f) fatias.push(f);
  }
  const resto = pct.filter((p) => !destaque.includes(p.codigo)).reduce((s, p) => s + p.frac, 0);
  if (resto > 0) fatias.push({ codigo: OUTRAS, frac: resto });
  return fatias;
}

export function nomeDaFatia(codigo: number): string {
  return codigo === OUTRAS ? 'Outras classes' : classeMapbiomas(codigo).nome;
}

export function corDaFatia(codigo: number): string {
  return codigo === OUTRAS ? '#5b6270' : classeMapbiomas(codigo).cor;
}

/** "62% Pastagem · 38% Formação Florestal" (as classes de 5% ou mais, até 3). */
export function resumoDoAno(comp: ComposicaoAno): string {
  const pct = porcentagens(comp).filter((p) => p.frac >= 0.05);
  if (!pct.length) return 'sem classe predominante';
  return pct
    .slice(0, 3)
    .map((p) => `${Math.round(p.frac * 100)}% ${classeMapbiomas(p.codigo).nome}`)
    .join(' · ');
}

/** Fração de vegetação nativa (formações florestais e não florestais) no ano. */
export function fracNativa(comp: ComposicaoAno): number | null {
  const pct = porcentagens(comp);
  if (!pct.length) return null;
  return pct
    .filter((p) => {
      const g = classeMapbiomas(p.codigo).grupo;
      return g === 'floresta' || g === 'campo';
    })
    .reduce((s, p) => s + p.frac, 0);
}

const STATUS_CAR: Record<string, string> = {
  AT: 'Ativo',
  PE: 'Pendente',
  SU: 'Suspenso',
  CA: 'Cancelado',
};

const TIPO_CAR: Record<string, string> = {
  IRU: 'Imóvel rural',
  AST: 'Assentamento da reforma agrária',
  PCT: 'Território de povos e comunidades tradicionais',
};

export function statusCar(i: ImovelCar): string {
  return (i.status && STATUS_CAR[i.status]) || i.status || 'Situação não informada';
}

export function tipoCar(i: ImovelCar): string {
  return (i.tipo && TIPO_CAR[i.tipo]) || i.tipo || 'Tipo não informado';
}
